import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    LayoutDashboard,
    Users,
    Building2,
    ShieldCheck,
    Wrench,
    X,
    Loader2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import { ScrollArea } from '../ui/scroll-area';
import type { QuadrantItemData } from '../dashboard/QuadrantItem';
import { useQuadrantItems } from '../../hooks/useQuadrantItems';
import { getEntityIcon } from '../../utils/navigationIconUtils';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { categoryColors, dashboardAccent } from '../../theme/palette';
import type { RecentPage } from '../../App';

interface QuadrantConfig {
    title: string;
    icon: LucideIcon;
    color: string;
    quadrant: number;
}

const QUADRANTS: QuadrantConfig[] = [
    { title: 'People', icon: Users, color: categoryColors.people, quadrant: 0 },
    { title: 'Core & Places', icon: Building2, color: categoryColors.corePlaces, quadrant: 1 },
    { title: 'Administration', icon: ShieldCheck, color: categoryColors.admin, quadrant: 2 },
    { title: 'Tools & Meta', icon: Wrench, color: categoryColors.toolsMeta, quadrant: 3 },
];

const RAIL_WIDTH = 56;
const FLYOUT_WIDTH = 340;

const formatCount = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 10_000) return `${(n / 1_000).toFixed(0)}k`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return n.toString();
};

const SUBPATH_TOOLTIP_MAX = 28;

function getRecentPageTooltip(page: RecentPage): string {
    const path = page.pathAfterCategory;
    if (path?.length) {
        const segment = path[0].toLowerCase();
        if (segment === 'person' && path.length === 1 && page.label) return page.label;
        const entityLabel = getSingularLabel(path[0]);
        const subpath = path.slice(1);
        if (subpath.length) {
            const subpathLabel = subpath.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' / ');
            const truncated = subpathLabel.length > SUBPATH_TOOLTIP_MAX
                ? subpathLabel.slice(0, SUBPATH_TOOLTIP_MAX).trim() + '…'
                : subpathLabel;
            return `${entityLabel} / ${truncated}`;
        }
        return entityLabel;
    }
    return getSingularLabel(page.icon);
}

interface SidebarProps {
    onNavigate: (
        destination: string,
        isEntity?: boolean,
        category?: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin'
    ) => void;
    onNavigateHash?: (hash: string) => void;
    selectedDestination?: string;
    activePageKey?: string;
    authResolved: boolean;
    recentPages?: RecentPage[];
    onDismissRecentPage?: (destination: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
    onNavigate,
    onNavigateHash,
    selectedDestination,
    activePageKey,
    authResolved,
    recentPages = [],
    onDismissRecentPage,
}) => {
    const [openQuadrant, setOpenQuadrant] = useState<number | null>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLDivElement>(null);

    const { quadrantItems } = useQuadrantItems({ onNavigate, authResolved });

    const groups = useMemo(() => {
        const g: QuadrantItemData[][] = [[], [], [], []];
        quadrantItems.forEach(item => {
            const q = Math.min(item.quadrant, 3);
            g[q].push(item);
        });
        return g;
    }, [quadrantItems]);

    const activeQuadrantFromDestination = useMemo(() => {
        if (!selectedDestination || selectedDestination === 'dashboard') return null;
        for (const item of quadrantItems) {
            const dest = selectedDestination.toLowerCase();
            const itemId = item.id.toLowerCase();
            if (itemId.includes(dest) || dest.includes(item.label.toLowerCase().replace(/\s+/g, ''))) {
                return item.quadrant;
            }
        }
        return null;
    }, [selectedDestination, quadrantItems]);

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (
            openQuadrant !== null &&
            flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
            railRef.current && !railRef.current.contains(e.target as Node)
        ) {
            setOpenQuadrant(null);
        }
    }, [openQuadrant]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpenQuadrant(null);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    const handleItemClick = (item: QuadrantItemData) => {
        item.onClick();
        setOpenQuadrant(null);
    };

    const activeQuadrantConfig = openQuadrant !== null ? QUADRANTS[openQuadrant] : null;

    return (
        <TooltipProvider delayDuration={200}>
            {/* Rail */}
            <div
                ref={railRef}
                className="flex flex-col items-center pt-4 gap-2 bg-sidebar border-r border-sidebar-border shrink-0 overflow-y-auto overflow-x-hidden box-border"
                style={{
                    width: RAIL_WIDTH,
                    minWidth: RAIL_WIDTH,
                    height: 'calc(100vh - 56px)',
                    position: 'sticky',
                    top: 56,
                    zIndex: 180,
                }}
            >
                {/* Dashboard home */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={() => onNavigateHash?.('dashboard')}
                            onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open('#dashboard', '_blank'); } }}
                            className="w-9 h-9 rounded-[10px] flex items-center justify-center mb-2 transition-all duration-150 hover:scale-110 active:scale-95 border"
                            style={{
                                backgroundColor: `${dashboardAccent}1f`,
                                borderColor: `${dashboardAccent}40`,
                            }}
                        >
                            <LayoutDashboard size={20} style={{ color: dashboardAccent }} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Dashboard</TooltipContent>
                </Tooltip>

                {/* Separator */}
                <div className="w-6 h-px bg-sidebar-border mb-1" />

                {/* Recent pages */}
                {recentPages.length > 0 && (
                    <>
                        {recentPages.map((page) => {
                            const PageIcon = getEntityIcon(page.icon);
                            const isActive = page.destination === activePageKey || page.destination === selectedDestination;
                            return (
                                <Tooltip key={page.destination}>
                                    <TooltipTrigger asChild>
                                        <div className="relative group">
                                            <button
                                                onClick={() => { if (!isActive) onNavigateHash?.(page.hash); }}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(page.hash, '_blank'); } }}
                                                className={cn(
                                                    'w-9 h-9 rounded-[10px] flex items-center justify-center transition-all duration-150',
                                                    isActive ? 'cursor-default' : 'cursor-pointer hover:scale-110 active:scale-95',
                                                )}
                                                style={{
                                                    backgroundColor: isActive ? `${page.color}30` : `${page.color}15`,
                                                    border: isActive ? `2px solid ${page.color}` : `1px solid ${page.color}30`,
                                                    boxShadow: isActive ? `0 0 8px ${page.color}40` : 'none',
                                                }}
                                            >
                                                <PageIcon size={18} style={{ color: page.color, opacity: isActive ? 1 : 0.85 }} />
                                            </button>
                                            {onDismissRecentPage && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onDismissRecentPage(page.destination); }}
                                                    className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center bg-muted border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-[2]"
                                                >
                                                    <X size={8} className="text-muted-foreground" />
                                                </button>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="right">{getRecentPageTooltip(page)}</TooltipContent>
                                </Tooltip>
                            );
                        })}
                        <div className="w-6 h-px bg-sidebar-border my-1" />
                    </>
                )}

                {/* Category icons */}
                {QUADRANTS.map((q) => {
                    const isFlyoutOpen = openQuadrant === q.quadrant;
                    const isCurrentPage = activeQuadrantFromDestination === q.quadrant;
                    const isHighlighted = isFlyoutOpen || isCurrentPage;
                    const hasItems = groups[q.quadrant].length > 0;
                    if (!hasItems) return null;
                    const QIcon = q.icon;
                    return (
                        <Tooltip key={q.quadrant}>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() => setOpenQuadrant(isFlyoutOpen ? null : q.quadrant)}
                                    className="w-9 h-9 rounded-[10px] flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-95"
                                    style={{
                                        backgroundColor: isHighlighted ? `${q.color}25` : 'transparent',
                                        borderLeft: isHighlighted ? `3px solid ${q.color}` : '3px solid transparent',
                                        borderRight: 'none',
                                        borderTop: 'none',
                                        borderBottom: 'none',
                                    }}
                                >
                                    <QIcon size={20} style={{ color: isHighlighted ? q.color : 'hsl(var(--sidebar-foreground))' }} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right">{q.title}</TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>

            {/* Flyout panel */}
            {openQuadrant !== null && activeQuadrantConfig && (
                <div
                    ref={flyoutRef}
                    className="fixed flex flex-col bg-sidebar border-r border-sidebar-border animate-in slide-in-from-left-2 duration-200 overflow-x-hidden box-border"
                    style={{
                        left: RAIL_WIDTH,
                        top: 56,
                        width: FLYOUT_WIDTH,
                        height: 'calc(100vh - 56px)',
                        zIndex: 195,
                        borderTop: `3px solid ${activeQuadrantConfig.color}`,
                        boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
                    }}
                >
                    {/* Flyout header */}
                    <div className="px-4 pt-4 pb-3 shrink-0">
                        <div className="flex items-center gap-2.5">
                            <div
                                className="w-8 h-8 rounded-md flex items-center justify-center"
                                style={{ backgroundColor: `${activeQuadrantConfig.color}18`, color: activeQuadrantConfig.color }}
                            >
                                <activeQuadrantConfig.icon size={18} />
                            </div>
                            <span
                                className="text-xs font-bold uppercase tracking-[1.5px]"
                                style={{ color: activeQuadrantConfig.color }}
                            >
                                {activeQuadrantConfig.title}
                            </span>
                        </div>
                    </div>

                    {/* Flyout items grid */}
                    <ScrollArea className="flex-1">
                        <div className="flex flex-wrap gap-3.5 px-3.5 pb-4 content-start">
                            {groups[openQuadrant].map((item) => {
                                const IconComp = item.icon;
                                return (
                                    <button
                                        key={item.id}
                                        className="flex flex-col items-center w-[86px] cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 active:scale-95 bg-transparent border-none p-0"
                                        onClick={() => handleItemClick(item)}
                                        onAuxClick={(e) => {
                                            if (e.button !== 1) return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const id = item.id ?? '';
                                            const url = id.startsWith('util-')
                                                ? `#utilities/${id.slice(5).toLowerCase()}`
                                                : `#${id}`;
                                            window.open(url, '_blank');
                                        }}
                                    >
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center relative transition-all duration-200 hover:shadow-lg"
                                            style={{
                                                border: `1.5px solid ${item.color}40`,
                                                background: `${item.color}12`,
                                            }}
                                        >
                                            <IconComp size={24} style={{ color: item.color }} />
                                            {item.countLoading ? (
                                                <Loader2 size={10} className="absolute -top-1 -right-1 animate-spin text-muted-foreground" />
                                            ) : item.count != null ? (
                                                <Badge
                                                    className="absolute -top-1.5 -right-1.5 text-[9px] px-1.5 min-w-[18px] h-4 flex items-center justify-center text-white border-0"
                                                    style={{ backgroundColor: item.color }}
                                                >
                                                    {formatCount(item.count)}
                                                </Badge>
                                            ) : null}
                                        </div>
                                        <span className="text-center mt-1 font-medium text-[11px] leading-tight max-w-[84px] line-clamp-2 text-muted-foreground">
                                            {item.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </div>
            )}

            {/* Backdrop overlay */}
            {openQuadrant !== null && (
                <div
                    onClick={() => setOpenQuadrant(null)}
                    className="fixed bg-black/10"
                    style={{
                        left: RAIL_WIDTH + FLYOUT_WIDTH,
                        top: 56,
                        right: 0,
                        bottom: 0,
                        zIndex: 185,
                    }}
                />
            )}
        </TooltipProvider>
    );
};
